// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useLang from "@/hooks/useLang";
import DashboardLayout from "@/UI/components/DashboardLayout";
import PopupPaper from "@/UI/components/PopupPaper";
import RoleRoute from "@/UI/components/RoleRoute";
import CrmScreen from "@/UI/screens/CrmScreen";
import InstallmentsScreen from "@/UI/screens/InstallmentsScreen";
import CustomerScreen from "@/UI/screens/CustomerScreen";
import CustomersScreen from "@/UI/screens/CustomersScreen";
import DriveScreen from "@/UI/screens/DriveScreen";
import LeadScreen from "@/UI/screens/LeadScreen";
import HomeScreen from "@/UI/screens/HomeScreen";
import LoginScreen from "@/UI/screens/LoginScreen";
import NotFoundScreen from "@/UI/screens/NotFoundScreen";
import ProfileScreen from "@/UI/screens/ProfileScreen";
import ProjectScreen from "@/UI/screens/ProjectScreen";
import PropertiesScreen from "@/UI/screens/PropertiesScreen";
import SettingsScreen from "@/UI/screens/SettingsScreen";
import SubscriptionScreen from "@/UI/screens/SubscriptionScreen";
import SupportScreen from "@/UI/screens/SupportScreen";
import UnitScreen from "@/UI/screens/UnitScreen";
import TeamScreen from "@/UI/screens/TeamScreen";
import TrashScreen from "@/UI/screens/TrashScreen";
import SelectOrganizationScreen from "@/UI/screens/SelectOrganizationScreen";
// --- Logic --------------------------------------------------------------------------------------
function RootLayout() {
    return (
        <>
            <Outlet />
            <PopupPaper />
        </>
    );
}

const router = createBrowserRouter([
    {
        element: <RootLayout />,
        children: [
            { path: "/login", element: <LoginScreen /> },
            { path: "/select-organization", element: <SelectOrganizationScreen /> },
            {
                path: "/",
                element: <DashboardLayout />,
                children: [
                    { index: true, element: <HomeScreen /> },
                    /**
                     * One `properties` segment, three screens. The unit sits under its project in the
                     * path because that is how a person walks in — and the back button then walks them
                     * back out the way they came.
                     *
                     * **The segments are names, not ids.** `/properties/telal-al-rabwah/units/A-101`
                     * is a link somebody can read, say out loud and recognise in their own history;
                     * the id version could only be told apart from any other by a machine. Both are
                     * still accepted by the API, so an old bookmark keeps working — see
                     * `backend/src/functions/findProperty`.
                     */
                    {
                        path: "properties",
                        element: (
                            <RoleRoute model="properties">
                                <PropertiesScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "properties/:slug",
                        element: (
                            <RoleRoute model="properties">
                                <ProjectScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "properties/:slug/units/:number",
                        element: (
                            <RoleRoute model="properties">
                                <UnitScreen />
                            </RoleRoute>
                        ),
                    },
                    /**
                     * One `crm` segment, four screens. **The people are the front door**: the
                     * section is called the customers, the first tab is the customers, and an
                     * enquiry is something one of them has — so `/crm` is the directory and the
                     * pipeline lives at `/crm/leads`.
                     *
                     * A lead's path does not nest under its customer: most are opened from the
                     * pipeline, not from the person.
                     */
                    {
                        path: "crm",
                        element: (
                            <RoleRoute model="crm">
                                <CustomersScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        // The money, under `crm` because an installment is a fact about a customer.
                        path: "crm/installments",
                        element: (
                            <RoleRoute model="sales">
                                <InstallmentsScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "crm/leads",
                        element: (
                            <RoleRoute model="crm">
                                <CrmScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "crm/leads/:id",
                        element: (
                            <RoleRoute model="crm">
                                <LeadScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "crm/customers",
                        element: (
                            <RoleRoute model="crm">
                                <CustomersScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "crm/customers/:id",
                        element: (
                            <RoleRoute model="crm">
                                <CustomerScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "drive",
                        element: (
                            <RoleRoute model="drive">
                                <DriveScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "team",
                        element: (
                            <RoleRoute model="team">
                                <TeamScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        /**
                         * Every role, deliberately: a member hits problems too. `support` is not in
                         * `CLIENT_SUPER_MODELS` — the API narrows the rows, not the door.
                         */
                        path: "support",
                        element: (
                            <RoleRoute model="support">
                                <SupportScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "trash",
                        element: (
                            <RoleRoute model="trash">
                                <TrashScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "settings",
                        element: (
                            <RoleRoute model="settings">
                                <SettingsScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        /** Owner and manager only — `subscription` is in `CLIENT_SUPER_MODELS`. */
                        path: "subscription",
                        element: (
                            <RoleRoute model="subscription">
                                <SubscriptionScreen />
                            </RoleRoute>
                        ),
                    },
                    /** The account's own page — every role has one, so it carries no role gate. */
                    { path: "profile", element: <ProfileScreen /> },
                ],
            },
            { path: "*", element: <NotFoundScreen /> },
        ],
    },
]);

export default function Router() {
    const lang = useLang((state) => state.lang) ?? "ar";

    useEffect(() => {
        document.documentElement.lang = lang;
        document.documentElement.dir = constants.rtlLanguages.includes(lang as "ar") ? "rtl" : "ltr";
    }, [lang]);

    return <RouterProvider router={router} />;
}
